import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./Navbar.css";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from "@mui/icons-material/Language";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FlightIcon from "@mui/icons-material/Flight";
import { AIRPORTS, REGIONAL_AIRSPACE } from "../constants";
import SearchModal from "./SearchModal";
import CountryInfo from "./CountryInfo";
import ShareQR from "./ShareQR";

function Navbar({
  selectedHub,
  onSelectHub,
  onSearch,
  searchError,
  onClearSearchError,
  searchModalOpen,
  onSearchModalChange,
  flightsData,
  regionalFlightsData,
  onLogoHome,
  onBookFlightOpen,
}) {
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [airportsExpanded, setAirportsExpanded] = useState(true);
  const [bookingExpanded, setBookingExpanded] = useState(false);
  const [languageExpanded, setLanguageExpanded] = useState(false);
  const [countryInfoOpen, setCountryInfoOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const dropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);

  const toggleLanguage = useCallback(() => {
    const newLang = i18n.language === "en" ? "fr" : "en";
    i18n.changeLanguage(newLang);
  }, [i18n]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setCountryDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = useCallback(
    (option) => {
      onSelectHub(option.name);
      setDropdownOpen(false);
      setMobileMenuOpen(false);
      setAirportsExpanded(true);
      setLanguageExpanded(false);
    },
    [onSelectHub],
  );

  const handleLogoClick = useCallback(() => {
    if (onLogoHome) {
      onLogoHome();
    } else {
      onSelectHub(REGIONAL_AIRSPACE.hubCode);
    }
  }, [onLogoHome, onSelectHub]);

  const handleSearchClick = useCallback(() => {
    onSearchModalChange(true);
  }, [onSearchModalChange]);

  const handleSearchClose = useCallback(() => {
    onSearchModalChange(false);
    // Restore previous hub when modal is closed without search
    if (onSearch) {
      onSearch("");
    }
  }, [onSearch, onSearchModalChange]);

  const handleSearch = useCallback(
    (query) => {
      if (onSearch) {
        onSearch(query);
      }
      // Close mobile menu after search
      setMobileMenuOpen(false);
    },
    [onSearch],
  );

  // Reset expanded states when menu opens
  useEffect(() => {
    if (mobileMenuOpen) {
      setAirportsExpanded(false);
      setLanguageExpanded(false);
    }
  }, [mobileMenuOpen]);

  const handleCountrySelect = useCallback((countryCode) => {
    setSelectedCountry(countryCode);
    setCountryInfoOpen(true);
    setMobileMenuOpen(false);
  }, []);

  return (
    <nav className="navbar">
      {/* Mobile Search Button */}
      <button className="mobile-search-button" onClick={handleSearchClick}>
        <SearchIcon fontSize="small" />
      </button>

      <div className="navbar-left">
        <button
          className="desktop-search-button"
          onClick={handleSearchClick}
          title="Search flights"
        >
          <SearchIcon fontSize="small" />
        </button>
      </div>

      <div className="navbar-center">
        {/* Desktop dropdowns hidden - only shown in mobile menu */}
        <div
          className="dropdown-container desktop-dropdown desktop-dropdown-hidden"
          ref={dropdownRef}
        >
          <button
            className="nav-link-button"
            onMouseEnter={() => {
              if (window.innerWidth > 480) {
                setDropdownOpen(true);
                setCountryDropdownOpen(false);
              }
            }}
            onClick={() => {
              setDropdownOpen(!dropdownOpen);
              setCountryDropdownOpen(false);
            }}
          >
            <span className="dropdown-text">{t("navbar.trackedAirports")}</span>
          </button>

          {dropdownOpen && (
            <div
              className="dropdown-menu"
              onMouseEnter={() => {
                if (window.innerWidth > 480) {
                  setDropdownOpen(true);
                  setCountryDropdownOpen(false);
                }
              }}
              onMouseLeave={() => {
                if (window.innerWidth > 480) {
                  setDropdownOpen(false);
                }
              }}
            >
              <button
                className="dropdown-item"
                onClick={() => {
                  onBookFlightOpen();
                  setDropdownOpen(false);
                }}
              >
                <span className="dropdown-item-icon">
                  <FlightIcon fontSize="small" />
                </span>
                <span>{t("dashboard.compareFlights")}</span>
              </button>
            </div>
          )}
        </div>

        <div
          className="logo"
          onClick={handleLogoClick}
          style={{ cursor: "pointer" }}
        >
          <span className="logo-text">RenuAirHub</span>
        </div>

        {/* Desktop Country Info Dropdown */}
        <div
          className="dropdown-container desktop-dropdown desktop-dropdown-hidden"
          ref={countryDropdownRef}
        >
          <button
            className="nav-link-button"
            onMouseEnter={() => {
              if (window.innerWidth > 480) {
                setCountryDropdownOpen(true);
                setDropdownOpen(false);
              }
            }}
            onClick={() => {
              setCountryDropdownOpen(!countryDropdownOpen);
              setDropdownOpen(false);
            }}
          >
            <span className="dropdown-text">
              {t("navbar.learnAboutCountries")}
            </span>
          </button>

          {countryDropdownOpen && (
            <div
              className="dropdown-menu"
              onMouseEnter={() => {
                if (window.innerWidth > 480) {
                  setCountryDropdownOpen(true);
                  setDropdownOpen(false);
                }
              }}
              onMouseLeave={() => {
                if (window.innerWidth > 480) {
                  setCountryDropdownOpen(false);
                }
              }}
            >
              <div className="dropdown-section-title">
                {t("navbar.westAfricanCountries")}
              </div>
              {AIRPORTS.filter((a) => a.region === "west").map((airport) => (
                <button
                  key={airport.iataCode}
                  className="dropdown-item dropdown-item-country"
                  onClick={() => {
                    handleCountrySelect(airport.iataCode);
                    setCountryDropdownOpen(false);
                  }}
                >
                  <span>{airport.country}</span>
                </button>
              ))}
              {AIRPORTS.some((a) => a.region === "north") && (
                <>
                  <div className="dropdown-divider" />
                  <div className="dropdown-section-title">
                    {t("navbar.northAfricanCountries")}
                  </div>
                  {AIRPORTS.filter((a) => a.region === "north").map(
                    (airport) => (
                      <button
                        key={airport.iataCode}
                        className="dropdown-item dropdown-item-country"
                        onClick={() => {
                          handleCountrySelect(airport.iataCode);
                          setCountryDropdownOpen(false);
                        }}
                      >
                        <span>{airport.country}</span>
                      </button>
                    ),
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="navbar-right">
        <button
          className="language-toggle desktop-language-toggle"
          onClick={toggleLanguage}
        >
          <LanguageIcon fontSize="small" />
          <span className="language-text">
            {i18n.language === "en" ? "FR" : "EN"}
          </span>
        </button>
      </div>

      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-button"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        <MenuIcon fontSize="small" />
      </button>

      {/* Mobile Full-Screen Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu-header">
            <button className="mobile-menu-search" onClick={handleSearchClick}>
              <SearchIcon fontSize="small" />
            </button>
            <div className="mobile-menu-logo">
              <span>RenuAirHub</span>
            </div>
            <button
              className="mobile-menu-close"
              onClick={() => setMobileMenuOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <div className="mobile-menu-content">
            {/* Book a Flight Section */}
            <div className="mobile-menu-section">
              <button
                className="mobile-menu-section-header"
                onClick={() => setBookingExpanded(!bookingExpanded)}
              >
                <span>{t("navbar.trackedAirports")}</span>
                <KeyboardArrowDownIcon
                  className={`mobile-menu-arrow ${bookingExpanded ? "expanded" : ""}`}
                />
              </button>
              {bookingExpanded && (
                <div className="mobile-menu-section-content">
                  <button
                    className="mobile-menu-item"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onBookFlightOpen();
                    }}
                  >
                    <span className="mobile-menu-item-icon">
                      <FlightIcon fontSize="small" />
                    </span>
                    <span>{t("dashboard.compareFlights")}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Track Flights Section */}
            <div className="mobile-menu-section">
              <button
                className="mobile-menu-section-header"
                onClick={() => setAirportsExpanded(!airportsExpanded)}
              >
                <span>{t("navbar.trackFlights")}</span>
                <KeyboardArrowDownIcon
                  className={`mobile-menu-arrow ${airportsExpanded ? "expanded" : ""}`}
                />
              </button>
              {airportsExpanded && (
                <div className="mobile-menu-section-content">
                  <div className="mobile-menu-subsection-title">
                    {t("navbar.westAfricanAirports")}
                  </div>
                  {AIRPORTS.filter((a) => a.region === "west").map(
                    (airport) => (
                      <div
                        key={airport.iataCode}
                        className="mobile-menu-hub-row"
                      >
                        <button
                          className={`mobile-menu-item mobile-menu-item-hub ${selectedHub === airport.hubCode ? "selected" : ""}`}
                          onClick={() =>
                            handleSelect({ name: airport.hubCode })
                          }
                        >
                          <img
                            src={`/images/flags/${airport.flagCode}.png`}
                            alt={airport.country}
                            className="mobile-menu-flag"
                            width="20"
                            height="15"
                          />
                          <span>{airport.hubCode}</span>
                        </button>
                        <button
                          className="mobile-menu-learn-btn"
                          onClick={() => handleCountrySelect(airport.iataCode)}
                          aria-label={`${t("countryInfo.learnMore")} - ${airport.country}`}
                        >
                          {t("countryInfo.learnMore")}
                        </button>
                      </div>
                    ),
                  )}
                  {AIRPORTS.some((a) => a.region === "north") && (
                    <>
                      <div className="mobile-menu-subsection-title">
                        {t("navbar.northAfricanAirports")}
                      </div>
                      {AIRPORTS.filter((a) => a.region === "north").map(
                        (airport) => (
                          <div
                            key={airport.iataCode}
                            className="mobile-menu-hub-row"
                          >
                            <button
                              className={`mobile-menu-item mobile-menu-item-hub ${selectedHub === airport.hubCode ? "selected" : ""}`}
                              onClick={() =>
                                handleSelect({ name: airport.hubCode })
                              }
                            >
                              <img
                                src={`/images/flags/${airport.flagCode}.png`}
                                alt={airport.country}
                                className="mobile-menu-flag"
                                width="20"
                                height="15"
                              />
                              <span>{airport.hubCode}</span>
                            </button>
                            <button
                              className="mobile-menu-learn-btn"
                              onClick={() =>
                                handleCountrySelect(airport.iataCode)
                              }
                              aria-label={`${t("countryInfo.learnMore")} - ${airport.country}`}
                            >
                              {t("countryInfo.learnMore")}
                            </button>
                          </div>
                        ),
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Language Section */}
            <div className="mobile-menu-section">
              <button
                className="mobile-menu-section-header"
                onClick={() => setLanguageExpanded(!languageExpanded)}
              >
                <span>{t("navbar.language")}</span>
                <KeyboardArrowDownIcon
                  className={`mobile-menu-arrow ${languageExpanded ? "expanded" : ""}`}
                />
              </button>
              {languageExpanded && (
                <div className="mobile-menu-section-content">
                  <button
                    className={`mobile-menu-language-item ${i18n.language === "en" ? "selected" : ""}`}
                    onClick={() => {
                      i18n.changeLanguage("en");
                    }}
                  >
                    <span>English</span>
                  </button>
                  <button
                    className={`mobile-menu-language-item ${i18n.language === "fr" ? "selected" : ""}`}
                    onClick={() => {
                      i18n.changeLanguage("fr");
                    }}
                  >
                    <span>Français</span>
                  </button>
                </div>
              )}
            </div>

            {/* Share Section */}
            <div className="mobile-menu-section mobile-menu-share-section">
              <ShareQR />
            </div>
          </div>
        </div>
      )}

      <SearchModal
        isOpen={searchModalOpen}
        onClose={handleSearchClose}
        onSearch={handleSearch}
        error={searchError}
        onClearError={onClearSearchError}
        flightsData={flightsData}
        regionalFlightsData={regionalFlightsData}
      />

      <CountryInfo
        isOpen={countryInfoOpen}
        onClose={() => setCountryInfoOpen(false)}
        countryCode={selectedCountry}
      />
    </nav>
  );
}

export default Navbar;
